import type { ChangeEvent, KeyboardEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import { AIButton, AIChipButton } from "@similarweb/ai-kit";
import { ArrowUp } from "@similarweb/icons";
import { AlertBanner, Button, CountryChipItem } from "@similarweb/ui-components";

import { CountryFilter as CountryFilterSelector } from "app/components/filters-bar/country-filter/CountryFilter";
import * as countryUtils from "app/components/filters-bar/utils";
import type { ICountryObject } from "app/services/CountryService";

import { NoSegmentBudgetNotice } from "./SegmentBudgetNotices";
import {
    ComposerCard,
    ComposerCountryRow,
    ComposerTextArea,
    ComposerWidthNotice,
    StepCard,
    StepHeading,
    StepWrapper,
    SuggestionsGroup,
    SuggestionsHint,
    SuggestionsOrDivider,
    SuggestionsRow,
} from "./styledComponents";
import { translateWithFallback as t } from "./translateWithFallback";

const MAX_DESCRIPTION_LENGTH = 1000;

/** The chip's label is also what gets submitted as the market description, so a localized
 *  catalog sends the agent a localized prompt — the same thing a user typing in their own
 *  language already does. */
const SUGGESTION_PROMPTS = [
    { key: "market.builder.v2.compose.suggestion.sportEquipment", fallback: "sport equipment" },
    { key: "market.builder.v2.compose.suggestion.carResellers", fallback: "online car resellers" },
    { key: "market.builder.v2.compose.suggestion.runningShoes", fallback: "running shoes for women" },
    { key: "market.builder.v2.compose.suggestion.beautyBrands", fallback: "makeup and skincare brands" },
    { key: "market.builder.v2.compose.suggestion.ecoHotels", fallback: "premium eco hotels" },
    { key: "market.builder.v2.compose.suggestion.organicCotton", fallback: "organic cotton clothing brands" },
];

const renderCountryChip = (selectedCountry: ICountryObject) => (
    <CountryChipItem
        id={selectedCountry.id}
        text={selectedCountry.text}
        hideActionButton
    />
);

type SuggestionsColumnProps = {
    onDescriptionChange: (value: string) => void;
    onManualFallback: () => void;
};

/** Figma node 141:4403 — hint, chip row, "or", and the manual-fallback button are one flex
 *  group with a uniform 24px gap; the button lives here (not as a separate row outside the
 *  card) so that gap comes from `SuggestionsGroup` itself instead of matching padding across
 *  two unrelated boxes. */
const SuggestionsColumn = ({ onDescriptionChange, onManualFallback }: SuggestionsColumnProps) => (
    <SuggestionsGroup>
        <SuggestionsHint>
            {t("market.builder.v2.compose.suggestions.hint", "Not sure where to start? Try these:")}
        </SuggestionsHint>
        <SuggestionsRow>
            {SUGGESTION_PROMPTS.map(({ key, fallback }) => {
                const prompt = t(key, fallback);

                return (
                    <AIChipButton
                        key={key}
                        label={prompt}
                        variant="soft"
                        withShadow
                        onClick={() => onDescriptionChange(prompt)}
                    />
                );
            })}
        </SuggestionsRow>
        <SuggestionsOrDivider>{t("market.builder.v2.compose.suggestions.or", "or")}</SuggestionsOrDivider>
        <Button
            type="flat"
            data-automation="market-builder-v2-manual-fallback-btn"
            onClick={onManualFallback}
        >
            {t("market.builder.v2.manual.button", "Manually pick domains instead")}
        </Button>
    </SuggestionsGroup>
);

type ComposeStepProps = {
    onGenerate: (description: string) => void;
    onManualFallback: () => void;
    selectedCountry: ICountryObject;
    onSelectedCountryChange: (country: ICountryObject) => void;
    hasSegmentBudget: boolean;
    hasSegmentsModule: boolean;
    /** The prompt the agent rejected, prefilled so the user only has to rephrase it. */
    initialDescription?: string;
    rejectionMessage?: string | null;
    bannerAction?: { label: string; onClick: () => void } | null;
};

export const ComposeStep = ({
    onGenerate,
    onManualFallback,
    selectedCountry,
    onSelectedCountryChange,
    hasSegmentBudget,
    hasSegmentsModule,
    initialDescription = "",
    rejectionMessage = null,
    bannerAction = null,
}: ComposeStepProps) => {
    const [description, setDescription] = useState(initialDescription);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);

    // A textarea cannot size itself to its content, so the height is measured and reapplied on
    // every change — from the suggestion chips too, which set the description from outside.
    useEffect(() => {
        const textArea = textAreaRef.current;
        if (!textArea) {
            return;
        }

        textArea.style.height = "auto";
        textArea.style.height = `${textArea.scrollHeight}px`;
    }, [description]);

    const handleSubmit = useCallback(() => {
        const trimmed = description.trim();
        if (!trimmed) {
            return;
        }
        onGenerate(trimmed);
    }, [description, onGenerate]);

    // Enter submits and Shift+Enter breaks the line, the way every other chat composer behaves.
    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            handleSubmit();
        }
    };

    const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => setDescription(event.target.value);

    return (
        <StepWrapper data-automation="market-builder-v2-compose-step">
            <StepCard
                gap="40px"
                data-automation="market-builder-v2-compose-card"
            >
                {/* No separate eyebrow here — the page's own title above this card already reads
                    "Create a custom industry" (`MarketBuilder.tsx`'s `<Title>`), so repeating it
                    inside the card was a duplicate, not a second line of copy. */}
                <StepHeading>
                    {t("market.builder.v2.compose.title", "Describe the market. We'll find the websites.")}
                </StepHeading>
                {rejectionMessage && (
                    <ComposerWidthNotice data-automation="market-builder-v2-rejection-banner">
                        <AlertBanner
                            variant="error"
                            shadowed
                        >
                            <AlertBanner.Image />
                            <AlertBanner.Description>{rejectionMessage}</AlertBanner.Description>
                            {bannerAction && (
                                <Button
                                    type="flat"
                                    data-automation="market-builder-v2-rejection-banner-action"
                                    onClick={bannerAction.onClick}
                                >
                                    {bannerAction.label}
                                </Button>
                            )}
                        </AlertBanner>
                    </ComposerWidthNotice>
                )}
                {!hasSegmentBudget && <NoSegmentBudgetNotice hasSegmentsModule={hasSegmentsModule} />}
                <ComposerCard data-automation="market-builder-v2-composer-card">
                    <ComposerTextArea
                        ref={textAreaRef}
                        rows={1}
                        autoFocus
                        maxLength={MAX_DESCRIPTION_LENGTH}
                        value={description}
                        placeholder={t(
                            "market.builder.ai.placeholder",
                            "For example: luxury cars, running shoes, or a longer description of your market",
                        )}
                        data-automation="market-builder-v2-composer-input"
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                    />
                    <ComposerCountryRow data-automation="market-builder-v2-composer-country">
                        <CountryFilterSelector
                            height={32}
                            dropdownPopupWidth={243}
                            availableCountries={countryUtils.getCountries()}
                            selectedCountryIds={{ [selectedCountry.id]: true }}
                            // `changeCountry`'s declared type says `(itemId: number) => void`, but this
                            // component actually invokes it with the full clicked country object — the
                            // same object `renderButtonComponent` below receives (typed as `object`
                            // there). MarketBuilder.tsx's existing top-bar country filter relies on this
                            // exact same real behavior.
                            changeCountry={onSelectedCountryChange as unknown as (itemId: number) => void}
                            dropdownPopupHeight={190}
                            cssClassContainer="market-builder-v2-composer-country-filter"
                            renderButtonComponent={renderCountryChip}
                        />
                        <AIButton
                            variant="primary"
                            size="medium"
                            icon={<ArrowUp size="sm" />}
                            onClick={handleSubmit}
                        />
                    </ComposerCountryRow>
                </ComposerCard>
                <SuggestionsColumn
                    onDescriptionChange={setDescription}
                    onManualFallback={onManualFallback}
                />
            </StepCard>
        </StepWrapper>
    );
};
